import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq, not } from 'ember-truth-helpers';
import TableDrawer from 'garaje/components/table-drawer';

interface FilterOption {
  label: string;
  value: string;
}

interface HealthDashboardFilterDrawerSignature {
  Args: {
    onClose: () => void;
    // Time range
    timeRangeOptions: string[];
    selectedTimeRange: string;
    setTimeRange: (value: string) => void;
    // Location
    locationOptions: FilterOption[];
    selectedLocation: string;
    setLocation: (value: string) => void;
    isGlobalAdmin: boolean;
    // Integration (install)
    installOptions: FilterOption[];
    selectedInstall: string | null;
    setInstall: (value: string) => void;
    // Event type
    eventTypeOptions: FilterOption[];
    selectedEventType: string;
    setEventType: (value: string) => void;
    // Integration type
    integrationTypeOptions: FilterOption[];
    selectedIntegrationType: string;
    setIntegrationType: (value: string) => void;
    // Status
    statusOptions: FilterOption[];
    selectedStatus: string;
    setStatus: (value: string) => void;
  };
}

export default <template>
  <TableDrawer>
    {{! Header }}
    <div class="flex justify-between items-center p-6 border-b border-gray-100">
      <h3 class="text-base font-semibold mb-0">Filters</h3>
      <button type="button" class="text-carbon-40 hover:text-carbon-70" {{on "click" @onClose}}>
        {{svgJar "cross-black" height="16" width="16"}}
      </button>
    </div>

    {{! Filter fields }}
    <div class="flex flex-col gap-5 px-6 py-5">

      {{! Time range }}
      <div>
        <label class="block text-xs text-carbon-40 mb-1.5">Time range</label>
        <select
          class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
          {{on "change" (pick "target.value" @setTimeRange)}}
        >
          {{#each @timeRangeOptions as |opt|}}
            <option value={{opt}} selected={{eq opt @selectedTimeRange}}>{{opt}}</option>
          {{/each}}
        </select>
      </div>

      {{! Location }}
      <div>
        <label class="block text-xs text-carbon-40 mb-1.5">Location</label>
        <select
          class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={{not @isGlobalAdmin}}
          {{on "change" (pick "target.value" @setLocation)}}
        >
          {{#each @locationOptions as |opt|}}
            <option value={{opt.value}} selected={{eq opt.value @selectedLocation}}>{{opt.label}}</option>
          {{/each}}
        </select>
      </div>

      {{! Integration }}
      <div>
        <label class="block text-xs text-carbon-40 mb-1.5">Integration</label>
        <select
          class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
          {{on "change" (pick "target.value" @setInstall)}}
        >
          {{#each @installOptions as |opt|}}
            <option
              value={{opt.value}}
              selected={{if opt.value (eq opt.value @selectedInstall) (not @selectedInstall)}}
            >{{opt.label}}</option>
          {{/each}}
        </select>
      </div>

      {{! Integration type }}
      <div>
        <label class="block text-xs text-carbon-40 mb-1.5">Integration type</label>
        <select
          class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
          {{on "change" (pick "target.value" @setIntegrationType)}}
        >
          {{#each @integrationTypeOptions as |opt|}}
            <option value={{opt.value}} selected={{eq opt.value @selectedIntegrationType}}>{{opt.label}}</option>
          {{/each}}
        </select>
      </div>

      {{! Event type }}
      <div>
        <label class="block text-xs text-carbon-40 mb-1.5">Event type</label>
        <select
          class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
          {{on "change" (pick "target.value" @setEventType)}}
        >
          {{#each @eventTypeOptions as |opt|}}
            <option value={{opt.value}} selected={{eq opt.value @selectedEventType}}>{{opt.label}}</option>
          {{/each}}
        </select>
      </div>

      {{! Status }}
      <div>
        <label class="block text-xs text-carbon-40 mb-1.5">Status</label>
        <select
          class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
          {{on "change" (pick "target.value" @setStatus)}}
        >
          {{#each @statusOptions as |opt|}}
            <option value={{opt.value}} selected={{eq opt.value @selectedStatus}}>{{opt.label}}</option>
          {{/each}}
        </select>
      </div>

    </div>
  </TableDrawer>
</template> satisfies TOC<HealthDashboardFilterDrawerSignature>;
